// @flow
import {
  createSelector,
  createSlice,
  createAsyncThunk
} from '@reduxjs/toolkit';
import isEmpty from '@justpark/helpers/src/isEmpty/isEmpty';
import deleteUserVehicleApi from '@justpark/api/src/requests/deleteUserVehicle';
import loadUserVehicles from '@justpark/api/src/requests/loadUserVehicles';
import registrationLookupApi from '@justpark/api/src/requests/registrationLookup';
import createVehicle from '@justpark/api/src/requests/createVehicle';
import getRequestError from '@justpark/api/src/helpers/getRequestError';
import getRequestErrorData from '@justpark/api/src/helpers/getRequestErrorData';
import { notify } from '../helpers/BugsnagClient';

const debug = require('debug')('jp:reducers:vehicle');

export type Vehicle = {|
  id: ?number,
  make: string,
  model: string,
  modelId: number,
  registration: string,
  fuelType: ?string,
  isHired: ?string,
  hireCompany: ?string,
  isPrimary: boolean,
  createdAt: string,
  isNew: boolean,
  isActive: boolean,
  editing: boolean
|};

export type State = {|
  userAddingVehicle: boolean,
  displayAllVehicles: boolean,
  loadingVehicles: boolean,
  vehicleLookupFailed: boolean,
  createVehicleLoading: boolean,
  vehiclesLoaded: boolean,
  vehicleLookupFailedMsg: string,
  vehicles: Vehicle[],
  vehicleApiError: ?{|
    title: string,
    code: number,
    identifier: string
  |},
  deleteVehicleApiError: ?{|
    title: string,
    code: number,
    identifier: string
  |},
  loadVehicleError: string
|};

export const initialState: State = {
  userAddingVehicle: false,
  displayAllVehicles: false,
  loadingVehicles: false,
  vehicleLookupFailed: false,
  createVehicleLoading: false,
  vehiclesLoaded: false,
  vehicleLookupFailedMsg: '',
  vehicles: [],
  vehicleApiError: null,
  deleteVehicleApiError: null,
  loadVehicleError: ''
};

export const deleteUserVehicle = createAsyncThunk(
  'vehicle/deleteUserVehicle',
  async (payload, { extra: { jpApiClient }, rejectWithValue }) => {
    try {
      const request = deleteUserVehicleApi(payload);
      await jpApiClient(request);

      return { id: payload };
    } catch (error) {
      return rejectWithValue(getRequestErrorData(error));
    }
  }
);

export const loadVehicles = createAsyncThunk(
  'vehicle/loadVehicles',
  async (payload, { extra: { jpApiClient }, rejectWithValue }) => {
    try {
      const request = loadUserVehicles();
      const response = await jpApiClient(request);

      return response.data.data;
    } catch (error) {
      return rejectWithValue(getRequestError(error));
    }
  }
);

export const createUserVehicle = createAsyncThunk(
  'vehicle/createUserVehicle',
  async (payload, { extra: { jpApiClient }, rejectWithValue }) => {
    try {
      const { modelId, registration } = payload;
      const request = createVehicle(modelId, registration);
      const response = await jpApiClient(request);

      return response.data.data;
    } catch (error) {
      return rejectWithValue(getRequestErrorData(error));
    }
  }
);

export const registrationLookup = createAsyncThunk(
  'vehicle/registrationLookup',
  async (payload, { extra: { jpApiClient }, rejectWithValue }) => {
    try {
      const request = registrationLookupApi(payload);
      const response = await jpApiClient(request);

      return response.data.data;
    } catch (error) {
      return rejectWithValue(getRequestErrorData(error));
    }
  }
);

export const createUserVehicleFromRegistration = (registration: string) => (
  dispatch: any
) =>
  dispatch(registrationLookup(registration)).then((response) => {
    if (response.payload.id || response.payload.data?.code === 20000) {
      const apiId = response.payload.id;
      const defaultModelId = 701;
      const modelId = apiId || defaultModelId;

      return dispatch(
        createUserVehicle({
          modelId,
          registration
        })
      );
    }
    throw response;
  });

const getIdxByKey = (array: Array<any>, key: string, value: any) => {
  let indexOfTempItem = -1;
  array.find((item, idx) => {
    indexOfTempItem = idx;
    return item[key] === value;
  });
  return indexOfTempItem;
};

export const getIdxById = (array, id) => getIdxByKey(array, 'id', id);

const reducerName = 'vehicle';

const vehicleSlice = createSlice({
  name: reducerName,
  initialState,
  reducers: {
    setInitialState: (): State => ({
      ...initialState
    }),
    selectVehicle: (state: State, action: Action): State => {
      if (action.payload.id) {
        const { id } = action.payload;
        const { vehicles } = state;

        return {
          ...state,
          vehicles: [
            ...vehicles
              .filter((vehicle) => vehicle.isNew !== true)
              .map((vehicle) => ({
                ...vehicle,
                isActive: vehicle.id === id
              }))
          ]
        };
      }
      return state;
    },
    toggleAllVehicles: (state: State): State => ({
      ...state,
      displayAllVehicles: !state.displayAllVehicles
    }),
    acknowledgeVehicleApiError: (state: State): State => ({
      ...state,
      vehicleApiError: null,
      deleteVehicleApiError: null
    }),
    toggleUserAddVehicle: (state: State): State => ({
      ...state,
      userAddingVehicle: !state.userAddingVehicle,
      vehicleLookupFailed: false,
      vehicleLookupFailedMsg: ''
    }),

    cancelRegistrationInput: (state: State): State => ({
      ...state,
      userAddingVehicle: false
    })
  },
  extraReducers: (builder) => {
    builder.addCase(createUserVehicle.pending, (state: State): State => {
      debug(createUserVehicle.pending);
      return {
        ...state,
        createVehicleLoading: true,
        deleteVehicleApiError: null
      };
    });
    builder.addCase(
      createUserVehicle.fulfilled,
      (state: State, action: Action): State => {
        debug(createUserVehicle.fulfilled, action);

        if (action.payload?.id) {
          const createdVehicle = action.payload;

          const existingIds = [...state.vehicles].map((vehicle) => vehicle.id);
          const itemToAdd =
            existingIds.indexOf(createdVehicle.id) > -1 ? [] : [createdVehicle];

          const vehicles = [...itemToAdd, ...state.vehicles]
            .filter((vehicle) => vehicle.id !== null)
            .map((vehicle) => ({
              ...vehicle,
              isActive: createdVehicle.id === vehicle.id,
              editing: false
            }));

          return {
            ...state,
            createVehicleLoading: false,
            userAddingVehicle: false,
            vehicles: [...vehicles]
          };
        }

        notify(new Error('No vehicle in response'), {
          metaData: action
        });

        return {
          ...state,
          createVehicleLoading: false,
          vehicleApiError: action.payload
        };
      }
    );
    builder.addCase(
      createUserVehicle.rejected,
      (state: State, action: Action): State => {
        debug(createUserVehicle.rejected, action);

        if (action.payload.data) {
          return {
            ...state,
            vehicleApiError: action.payload.data,
            createVehicleLoading: false
          };
        }

        return {
          ...state,
          vehicleApiError: action.payload,
          createVehicleLoading: false
        };
      }
    );
    builder.addCase(deleteUserVehicle.pending, (state: State): State => {
      debug('deleteUserVehicle.pending');

      return { ...state, deleteVehicleApiError: null };
    });
    builder.addCase(
      deleteUserVehicle.fulfilled,
      (state: State, action: Action): State => {
        debug('deleteUserVehicle.fulfilled', action);

        const {
          payload: { id }
        } = action;

        if (id) {
          const { vehicles } = state;
          const newVehicles = [...vehicles];
          const idx = getIdxById(vehicles, id);

          newVehicles.splice(idx, 1);

          const userAddingVehicle = isEmpty(newVehicles);

          if (newVehicles.length >= 1) {
            const [first, ...otherNewVehicles] = newVehicles;
            return {
              ...state,
              vehicles: [
                {
                  ...first,
                  isActive: true
                },
                ...otherNewVehicles
              ],
              userAddingVehicle,
              deleteVehicleApiError: null
            };
          }

          return {
            ...state,
            vehicles: [...newVehicles],
            userAddingVehicle,
            deleteVehicleApiError: null
          };
        }

        notify(new Error('No vehicle id in delete action'), {
          metaData: action
        });

        return {
          ...state
        };
      }
    );
    builder.addCase(
      deleteUserVehicle.rejected,
      (state: State, action: Action): State => {
        debug('deleteUserVehicle.rejected', action);

        const {
          payload: { data }
        } = action;

        if (data) {
          return {
            ...state,
            deleteVehicleApiError: data
          };
        }
        notify(new Error('Invalid shape for vehicle delete'), {
          metaData: action
        });

        return {
          ...state,
          vehicleApiError: action.payload
        };
      }
    );
    builder.addCase(loadVehicles.pending, (state: State): State => {
      debug('loadVehicles.pending');

      return {
        ...state,
        loadingVehicles: true,
        loadVehicleError: '',
        deleteVehicleApiError: null
      };
    });
    builder.addCase(
      loadVehicles.fulfilled,
      (state: State, action: Action): State => {
        debug('loadVehicles.fulfilled', action);

        const { payload } = action;

        if (payload) {
          const rawVehicles = [...payload];
          const vehicles = rawVehicles.map((item) => ({
            ...item,
            isActive: false,
            editing: false,
            isNew: false
          }));

          if (vehicles[0]) {
            vehicles[0].isActive = true;
          }

          return {
            ...state,
            userAddingVehicle: isEmpty(vehicles),
            loadingVehicles: false,
            vehiclesLoaded: true,
            vehicles: [...vehicles]
          };
        }

        throw new Error('No listing in checkout listing details response');
      }
    );
    builder.addCase(
      loadVehicles.rejected,
      (state: State, action: Action): State => {
        debug('loadVehicles.rejected', action);

        return {
          ...state,
          loadingVehicles: false,
          vehiclesLoaded: false,
          userAddingVehicle: true,
          loadVehicleError: action.payload || 'There has been an error'
        };
      }
    );
    builder.addCase(registrationLookup.pending, (state: State): State => {
      debug(registrationLookup.pending);
      return {
        ...state,
        loadingVehicleLookup: true,
        vehicleLookupFailed: false,
        vehicleLookupFailedMsg: ''
      };
    });
    builder.addCase(
      registrationLookup.fulfilled,
      (state: State, action: Action): State => {
        debug(registrationLookup.fulfilled, action);

        if (action.payload.id && action.meta.arg) {
          const vehicle = {
            ...action.payload,
            id: null,
            modelId: action.payload.id,
            editing: true,
            isNew: true,
            isActive: false,
            registration: action.meta.arg
          };
          const newVehicles = [
            ...state.vehicles.map((item) => ({
              ...item,
              editing: false,
              isNew: false,
              isActive: false
            }))
          ];
          newVehicles.unshift(vehicle);
          return {
            ...state,
            userAddingVehicle: false,
            loadingVehicleLookup: false,
            vehicleLookupFailed: false,
            vehicleLookupFailedMsg: '',
            vehicles: newVehicles
          };
        }

        notify(new Error('No data in vehicle lookup response'), {
          metaData: action
        });

        return {
          ...state,
          loadingVehicleLookup: false
        };
      }
    );
    builder.addCase(
      registrationLookup.rejected,
      (state: State, action: Action): State => {
        debug(registrationLookup.rejected, action);

        if (action.meta.arg && action.payload.data?.code === 20000) {
          // vehicle not found by experian
          const otherVehicleId = 701;
          const vehicle = {
            id: null,
            modelId: otherVehicleId,
            make: 'Other',
            model: 'Other',
            editing: true,
            isNew: true,
            isActive: false,
            registration: action.meta.arg
          };

          const newVehicles = [
            ...state.vehicles.map((item) => ({
              ...item,
              editing: false,
              isNew: false,
              isActive: false
            }))
          ];
          newVehicles.unshift(vehicle);

          return {
            ...state,
            vehicles: newVehicles,
            userAddingVehicle: false,
            loadingVehicleLookup: false
          };
        }

        return {
          ...state,
          loadingVehicleLookup: false
        };
      }
    );
  }
});

export default vehicleSlice;

export const {
  setInitialState,
  selectVehicle,
  toggleUserAddVehicle,
  cancelRegistrationInput,
  acknowledgeVehicleApiError,
  toggleAllVehicles
} = vehicleSlice.actions;

export const selectVehicles = (state) => state[reducerName].vehicles;
export const selectVehiclesIsLoaded = (state) =>
  state[reducerName].vehiclesLoaded;
export const selectVehiclesIsLoading = (state) =>
  state[reducerName].loadingVehicles;
export const selectVehiclesAddVehicleApiErrorTitle = (state) =>
  state[reducerName].vehicleApiError?.title || '';
export const selectVehiclesDeleteVehicleApiErrorTitle = (state) =>
  state[reducerName].deleteVehicleApiError?.title || '';
export const selectVehiclesIsAddVehicleLoading = (state) =>
  state[reducerName].createVehicleLoading;
export const selectVehiclesIsLoadingVehicleError = (state) =>
  state[reducerName].loadVehicleError;

export const selectActiveVehicle = createSelector(selectVehicles, (vehicles) =>
  vehicles.find((item) => item.isActive)
);
